import {
    Badge,
    Button,
    FluentProvider,
    Input,
    Label,
    Tooltip,
    makeStyles,
    tokens,
    typographyStyles
} from "@fluentui/react-components";
import {
    ChevronLeft20Regular,
    Dismiss20Regular,
    Info16Regular,
    LockClosed20Regular
} from "@fluentui/react-icons";
import * as React from "react";
import StringsStore from "sh-strings/store";
import ThemeUtils from "sh-application/utility/ThemeUtils";
import { getGenericEventPropertiesObject } from "sh-instrumentation";
import { InstrumentationService } from "sh-services";

import { TeamSetupPickerStepProps } from "../TeamSetupPickerStepProps";
import { gotoNextSetupStep, gotoPrevSetupStep, setTeamSetupChannelName, setTeamSetupSchedulingGroupName, setTeamSetupTeamDisplayName } from "../store/store";

const DEFAULT_CHANNEL_NAME = "General";

const useStyles = makeStyles({
    // The card chrome (background, rounded corners, shadow) lives on `.root`. The wrapping
    // FluentProvider would otherwise paint its own opaque, square-cornered background of the
    // same color behind the card, making the rounded corners read as sharp. Keep it transparent.
    provider: {
        backgroundColor: "transparent",
        borderRadius: tokens.borderRadiusXLarge
    },
    root: {
        display: "flex",
        flexDirection: "column",
        rowGap: tokens.spacingVerticalS,
        padding: tokens.spacingHorizontalXXL,
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        borderRadius: tokens.borderRadiusXLarge,
        boxShadow: tokens.shadow64,
        // Fill the (responsive) dialog surface and stay within the viewport so the card
        // reflows without horizontal scrolling down to a 320px-wide viewport (WCAG 1.4.10).
        // Scrolls internally when vertical space is constrained (e.g. a 320px-tall viewport).
        width: "100%",
        maxWidth: "600px",
        height: "min(460px, 100vh)",
        maxHeight: "100vh",
        overflowY: "auto",
        boxSizing: "border-box"
    },
    header: {
        display: "flex",
        alignItems: "center",
        columnGap: tokens.spacingHorizontalS,
        width: "100%"
    },
    title: {
        flex: "1 1 auto",
        minWidth: 0,
        color: tokens.colorNeutralForeground1,
        ...typographyStyles.subtitle1
    },
    description: {
        margin: 0,
        color: tokens.colorNeutralForeground1,
        ...typographyStyles.body1
    },
    contents: {
        display: "flex",
        flexDirection: "column",
        rowGap: tokens.spacingVerticalL,
        paddingBottom: tokens.spacingVerticalS,
        width: "100%"
    },
    field: {
        display: "flex",
        flexDirection: "column",
        rowGap: tokens.spacingVerticalXXS,
        width: "100%"
    },
    labelRow: {
        display: "flex",
        alignItems: "center",
        columnGap: tokens.spacingHorizontalXXS
    },
    infoButton: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "16px",
        maxWidth: "16px",
        width: "16px",
        height: "16px",
        minHeight: "16px",
        padding: 0,
        backgroundColor: "transparent",
        border: "none",
        borderRadius: tokens.borderRadiusSmall,
        color: tokens.colorNeutralForeground2,
        cursor: "pointer",
        "&:focus-visible": {
            outline: `${tokens.strokeWidthThin} solid ${tokens.colorStrokeFocus2}`
        }
    },
    input: {
        width: "100%"
    },
    sensitivity: {
        display: "flex",
        flexDirection: "column",
        rowGap: tokens.spacingVerticalXS,
        alignItems: "flex-start"
    },
    // The sensitivity value is display-only (it can't be changed here), so it's rendered as a
    // static Badge rather than a Button — no hover/pressed feedback and not clickable. Give it a
    // button-like height and horizontal padding so the value text has room to breathe.
    sensitivityValue: {
        height: "32px",
        paddingLeft: tokens.spacingHorizontalM,
        paddingRight: tokens.spacingHorizontalM,
        borderRadius: "4px",
        border: `1px solid ${tokens.colorTransparentStroke}`,
        backgroundColor: tokens.colorNeutralBackground5,
        ...typographyStyles.caption1Strong
    },
    footer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        columnGap: tokens.spacingHorizontalM,
        width: "100%",
        paddingTop: tokens.spacingVerticalM,
        marginTop: "auto"
    },
    primaryGroup: {
        display: "flex",
        alignItems: "center",
        columnGap: tokens.spacingHorizontalS
    }
});

/**
 * Step "New team" in the unknown-user onboarding flow. Collects the team
 * name and the name of the first channel before the user advances to the
 * AddTeamMembers step. Wrapped in FluentProvider so Fluent v9 tokens follow
 * the Teams light/dark/HC theme.
 */
const CreateTeamName: React.FC<TeamSetupPickerStepProps> = (props) => {
    const styles = useStyles();
    const strings = StringsStore().registeredStringModules.get("teamSetupPicker")!.strings;
    const [teamName, setTeamName] = React.useState<string>("");
    const [channelName, setChannelName] = React.useState<string>(DEFAULT_CHANNEL_NAME);

    const logCreateTeamNameAction = (action: string) => {
        InstrumentationService.logEvent(InstrumentationService.events.ConvergedOnboarding, [
            getGenericEventPropertiesObject(InstrumentationService.properties.EventType, action)
        ]);
    };

    // Step-shown telemetry: emitted once when the "New team" step is first rendered.
    React.useEffect(() => {
        logCreateTeamNameAction(InstrumentationService.values.CreateTeamNameViewed);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleNext = () => {
        logCreateTeamNameAction(InstrumentationService.values.CreateTeamNameNextClicked);
        const trimmedChannelName = channelName.trim();
        setTeamSetupTeamDisplayName(teamName.trim());
        setTeamSetupSchedulingGroupName(trimmedChannelName);
        setTeamSetupChannelName(trimmedChannelName);
        gotoNextSetupStep();
    };

    const handleBack = () => {
        logCreateTeamNameAction(InstrumentationService.values.CreateTeamNameBackClicked);
        // Go back to the previous onboarding step (UnknownUserTeamPicker) rather than
        // cancelling out of the flow. Calling onCancel here would re-initialize the
        // setup sequence to SwitchTeams mode, leaving the picker in an empty state.
        gotoPrevSetupStep();
    };

    const handleClose = () => {
        logCreateTeamNameAction(InstrumentationService.values.CreateTeamNameClosed);
        if (props.onClose) {
            props.onClose();
        }
    };

    const isValid = teamName.trim().length > 0 && channelName.trim().length > 0;

    return (
        <FluentProvider theme={ThemeUtils.getFluentV2ShiftTheme()} className={styles.provider}>
            <div role="region" aria-label={strings.get("createTeamNameTitle")} className={styles.root}>
                <div className={styles.header}>
                    <span className={styles.title}>{strings.get("createTeamNameTitle")}</span>
                    <Button
                        appearance="transparent"
                        size="small"
                        icon={<Dismiss20Regular />}
                        aria-label={strings.get("createTeamNameCloseAriaLabel")}
                        onClick={handleClose}
                    />
                </div>

                <p className={styles.description}>
                    {strings.get("createTeamNameDescription")}
                </p>

                <div className={styles.contents}>
                    <div className={styles.field}>
                        <Label htmlFor="team-name-input" required size="small">
                            {strings.get("createTeamNameTeamNameLabel")}
                        </Label>
                        <Input
                            id="team-name-input"
                            className={styles.input}
                            appearance="filled-darker"
                            value={teamName}
                            placeholder={strings.get("createTeamNameTeamNamePlaceholder")}
                            onChange={(_, data) => setTeamName(data.value)}
                            autoFocus
                        />
                    </div>

                    <div className={styles.field}>
                        <div className={styles.labelRow}>
                            <Label htmlFor="channel-name-input" required size="small">
                                {strings.get("createTeamNameChannelLabel")}
                            </Label>
                            <Tooltip
                                content={strings.get("createTeamNameChannelInfoTooltip") || ""}
                                relationship="label"
                                withArrow
                            >
                                <Button type="button" className={styles.infoButton} aria-label={strings.get("createTeamNameChannelInfoAriaLabel")}>
                                    <Info16Regular />
                                </Button>
                            </Tooltip>
                        </div>
                        <Input
                            id="channel-name-input"
                            className={styles.input}
                            appearance="filled-darker"
                            value={channelName}
                            onChange={(_, data) => setChannelName(data.value)}
                        />
                    </div>

                    <div className={styles.sensitivity}>
                        <Label size="small">{strings.get("createTeamNameSensitivityLabel")}</Label>
                        <Badge
                            className={styles.sensitivityValue}
                            appearance="outline"
                            color="informative"
                            size="extra-large"
                            shape="rounded"
                            icon={<LockClosed20Regular />}
                        >
                            {strings.get("createTeamNameSensitivityValue")}
                        </Badge>
                    </div>
                </div>

                <div className={styles.footer}>
                    <Button
                        appearance="transparent"
                        icon={<ChevronLeft20Regular />}
                        onClick={handleBack}
                    >
                        {strings.get("backButtonLabel")}
                    </Button>
                    <div className={styles.primaryGroup}>
                        <Button
                            appearance="primary"
                            disabled={!isValid}
                            onClick={handleNext}
                        >
                            {strings.get("nextButtonLabel")}
                        </Button>
                    </div>
                </div>
            </div>
        </FluentProvider>
    );
};

export default CreateTeamName;
